<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';

import brokenPeanut from '@/assets/broken-peanut.png';
import PostalAddress from '@/components/base/PostalAddress.vue';
import TextButton from '@/components/base/TextButton.vue';
import { ImageUrlsByProductId } from '@/store/modules/cart';
import {
  buildTitleImage,
  containsGiftCertificate,
  hasOnlyGiftCertificate,
  Shipment,
} from '@/utils/cart';

const emit = defineEmits<{ 'edit-shipment': [string] }>();
const props = defineProps<{
  addressNumber: number;
  editable?: boolean;
  shipment: Shipment;
}>();

const store = useStore();

// support variants that don't have their own images
const listingImageUrlsByProductId = computed<ImageUrlsByProductId>(
  () => store.state.cartModule.listingImageUrlsByProductId,
);

const imageUrl = computed(
  () =>
    buildTitleImage(props.shipment.lineItems[0], listingImageUrlsByProductId.value)?.small ??
    brokenPeanut,
);

const itemsBubbleStyles = [
  'border',
  'border-black',
  'border-solid',
  'flex',
  'h-10',
  'items-center',
  'px-5',
  'py-2',
  'rounded-full',
  'w-max',
  'text-sm',
  'md:text-base',
];

const handleEditClick = () => {
  emit('edit-shipment', props.shipment.key);
};
</script>

<template>
  <div data-test="saved-shipment">
    <div class="flex content-center justify-between w-full mb-2">
      <strong class="text-base leading-5 md:text-lg" data-test="saved-shipment-address-label">
        Address {{ addressNumber }}
      </strong>
      <TextButton v-if="editable" class="text-sm" @click="handleEditClick"> Edit </TextButton>
    </div>
    <div
      v-if="!hasOnlyGiftCertificate(shipment.lineItems)"
      class="flex items-end justify-between w-full"
    >
      <PostalAddress :address="shipment.address" />
      <div :class="[itemsBubbleStyles, { 'cursor-pointer': editable }]" @click="handleEditClick">
        <img :src="imageUrl" :alt="shipment.lineItems[0].name.en" class="h-8 mr-1" />
        {{ shipment.lineItems.length }} Item{{ shipment.lineItems.length > 1 ? 's' : '' }}
      </div>
    </div>
    <div
      v-if="containsGiftCertificate(shipment.lineItems)"
      class="mb-4 text-sm leading-4 md:leading-5 md:text-base md:mb-5"
      :class="{ 'pt-4 md:pt-5': !hasOnlyGiftCertificate(shipment.lineItems) }"
    >
      <strong class="text-base leading-5 md:text-lg"> Gift Certificate Recipient </strong>
      <div v-if="hasOnlyGiftCertificate(shipment.lineItems)">{{ shipment.address.name }}</div>
      <div>{{ shipment.address.email }}</div>
    </div>
  </div>
</template>
